// NIGHT-DIRECTSTAFF-2026-10-28 §5: what the candidate will receive from the agents on this placement, from the placement row alone --
// so staff can see it before it goes. The agents' own rules (recruiter-agent, read 2026-10-28), not a copy of their sending:
//   week-1 check-in   week1-checkin.js: ONE message on day 5 after the start (sent day 5-7, for quiet hours / a missed tick), once the
//                     placement is marked Started; a text when they consented to texts, else an email.
//   re-placement      replacement.js: a 13-week contract (84-98 days) -> week 9 (start + 56 days); any other length -> 4 weeks before
//                     the end; one nudge a week later if no reply; never once the contract is within 7 days of its end.
//   review request    review-request.js: once the placement ends (or 3 days past its end date): one request for an honest review.
// Their switches (each "off" / "log" / "send") are theirs; the list says what each step does, the agents decide whether it runs.
import { daysTo } from './placements';

const DAY = 864e5;
const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const at = (d) => { const x = Date.parse(`${s(d).slice(0, 10)}T12:00:00Z`); return Number.isNaN(x) ? null : x; };
const iso = (ms) => new Date(ms).toISOString().slice(0, 10);
const dayText = (ms) => new Date(ms).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
const LIVE = new Set(['confirmed', 'started', 'ending', 'extended']);

/** -> [{ key, on: 'YYYY-MM-DD', when: 'Sat, Nov 1', what, how, past }] in date order; [] for a placement not on (or about to be on) contract */
export function checkinSchedule(p, now = Date.now()) {
  if (!p || !LIVE.has(s(p.status)) || p.placement_outcome) return [];
  const start = at(p.start_date); const end = at(p.end_date);
  if (start === null) return [];
  const out = [];
  const add = (key, ms, what, how) => out.push({ key, on: iso(ms), when: dayText(ms), what, how, past: ms < now - DAY });
  add('week1', start + 5 * DAY, 'First-week check-in: "How\'s the first week going? Anything you need from us?"',
    s(p.status) === 'confirmed' ? 'by text if they agreed to texts, else email — once the placement is marked Started' : 'by text if they agreed to texts, else email');
  if (end !== null && end > start) {
    const len = Math.round((end - start) / DAY);
    const open = len >= 84 && len <= 98 ? start + 56 * DAY : end - 28 * DAY;
    if (open >= start && open < end - 7 * DAY) {   // the agents act on a contract in progress: never before the start
      add('replacement', open, 'Re-placement: names the facility and the end date and asks: extend here, or find the next one?', 'by text, if they agreed to texts');
      if (open + 7 * DAY < end - 7 * DAY) add('replacement-nudge', open + 7 * DAY, 'One reminder of that, if no reply', 'by text');
    }
    add('review', end, 'A request for their honest review of us', 'by text if they agreed to texts, else email — after the placement ends');
  }
  return out.sort((a, b) => a.on.localeCompare(b.on));
}

/** Today (§5): Confirmed placements starting in the next 1-7 days (today's own start is "Placements starting") -> rows, soonest first */
export function startsThisWeek({ placements = [], candidates = [], now = Date.now() } = {}) {
  const byId = new Map((candidates || []).map((c) => [String(c.id), c]));
  const test = (c) => !!c && Array.isArray(c.tags) && c.tags.some((x) => s(x).toLowerCase() === 'test');
  const out = [];
  for (const p of placements || []) {
    if (s(p.status) !== 'confirmed' || !p.start_date) continue;
    const d = daysTo(p.start_date, now);
    if (d === null || d < 1 || d > 7) continue;
    const candidate = byId.get(String(p.candidate_id));
    if (!candidate || test(candidate) || candidate.merged_into) continue;
    out.push({ placement: p, candidate, daysTo: d });
  }
  return out.sort((a, b) => s(a.placement.start_date).localeCompare(s(b.placement.start_date)));
}
/** "#35 starts Sat, Nov 1 at Synthetic General (in 4 days) · week-1 check-in Thu, Nov 6" */
export function startsSoonLine(x) {
  const p = x.placement; const st = at(p.start_date);
  return `#${p.candidate_id} starts ${st === null ? p.start_date : dayText(st)}${p.facility ? ` at ${p.facility}` : ''} (in ${x.daysTo} day${x.daysTo === 1 ? '' : 's'})`
    + (st === null ? '' : ` · week-1 check-in ${dayText(st + 5 * DAY)}`);
}
